import { AlertTriangle, AtSign, LayoutGrid } from "lucide-react";
import { EmptyState, Progress } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { boardEndMs, byEndDescending, progressPercent, remainingLabel, urgencyColor } from "@/lib/format";
import { kindColor, type BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Every board, as the Boards tab's own screen — the phone and tablet stand-in for
 * the sidebar's board list, roomier because it has the whole width: each board's
 * window and time left, and the counts that say whether it needs looking at.
 * Running boards first, then the expired ones, which stay until archived. New board
 * is the top bar's +, the one place the tab layout puts a view's action.
 */
export function BoardsView({
  boards,
  now,
  currentBoardId,
  onOpen,
}: {
  /** Live boards only — archived ones are under More → Archive. */
  boards: BoardDetail[];
  now: number;
  /** The board the Boards tab returns to, marked so the list says where you were. */
  currentBoardId: string | null;
  onOpen: (boardId: string) => void;
}) {
  const running: BoardDetail[] = [];
  const expired: BoardDetail[] = [];
  for (const detail of boards) {
    (boardEndMs(detail.board.endsAt) < now ? expired : running).push(detail);
  }
  expired.sort(byEndDescending);

  return (
    <Page>
      <PageHeader
        title="Boards"
        showTitle={false}
        description={`${running.length} running${expired.length > 0 ? ` · ${expired.length} expired` : ""}`}
      />

      {boards.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid />}
          title="No boards yet"
          hint="Create a time-boxed board, or ask Claude to make one for you."
          className="py-12"
        />
      ) : null}

      <div className="grid gap-2 win-md:grid-cols-2">
        {running.map((detail) => (
          <BoardTile
            key={detail.board.id}
            detail={detail}
            now={now}
            current={detail.board.id === currentBoardId}
            onOpen={() => onOpen(detail.board.id)}
          />
        ))}
      </div>

      {expired.length > 0 ? (
        <>
          <div className="flex items-center gap-2 pb-2 pt-5">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Expired</p>
            <span className="h-px flex-1 bg-border/70" />
          </div>
          <div className="grid gap-2 win-md:grid-cols-2">
            {expired.map((detail) => (
              <BoardTile
                key={detail.board.id}
                detail={detail}
                now={now}
                current={detail.board.id === currentBoardId}
                onOpen={() => onOpen(detail.board.id)}
                dimmed
              />
            ))}
          </div>
        </>
      ) : null}
    </Page>
  );
}

const BoardTile = ({
  detail,
  now,
  current,
  dimmed,
  onOpen,
}: {
  detail: BoardDetail;
  now: number;
  current: boolean;
  dimmed?: boolean;
  onOpen: () => void;
}) => {
  const { board, stats, window: boardWindow } = detail;
  const tint = urgencyColor(boardWindow, now);
  const outstanding = stats.total - stats.done;
  return (
    <button
      onClick={onOpen}
      className={cn(
        "w-full rounded-lg border bg-card px-3.5 py-3 text-left transition-colors active:bg-muted/50",
        current ? "border-ring/50" : "border-border/70",
        dimmed && "opacity-80",
      )}
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-[14px] font-medium">{board.name}</p>
        {stats.overdue > 0 ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/15 px-1.5 text-[10.5px] font-semibold text-destructive [&_svg]:size-3">
            <AlertTriangle /> {stats.overdue} overdue
          </span>
        ) : null}
        {stats.openMentions > 0 ? (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 text-[10.5px] font-semibold [&_svg]:size-3"
            style={{ color: "var(--kind-review)", backgroundColor: "color-mix(in oklab, var(--kind-review) 16%, transparent)" }}
          >
            <AtSign /> {stats.openMentions}
          </span>
        ) : null}
      </div>
      <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <span className="truncate">{boardWindow.label}</span>
        <span className="shrink-0 font-medium tabular-nums" style={{ color: tint }}>
          {remainingLabel(boardWindow, now)}
        </span>
      </p>
      <Progress value={progressPercent(boardWindow, now)} indicatorColor={tint} className="mt-2 h-1" />
      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] tabular-nums text-muted-foreground">
        <span>{outstanding === 0 && stats.total > 0 ? "all clear" : `${outstanding} left`}</span>
        {stats.active > 0 ? <span style={{ color: kindColor("active") }}>{stats.active} doing</span> : null}
        {stats.blocked > 0 ? <span style={{ color: kindColor("blocked") }}>{stats.blocked} blocked</span> : null}
        {stats.review > 0 ? <span style={{ color: kindColor("review") }}>{stats.review} in review</span> : null}
        <span style={{ color: kindColor("done") }}>{stats.done} done</span>
      </p>
    </button>
  );
};
